import type { ResourceRate } from '../../../services/vendorService';
import { currency, perUnit } from './format';

/**
 * M3 - how a pending rate reads on the approvals screen: "New rate $175.00 / hours" for a
 * first rate (H6), or "$150.00 → $175.00 / hours (+16.7%)" for a change.
 */
export interface RateChangeSummary {
  isNew: boolean;
  priorRate: number | null;
  newRate: number;
  /** Percentage change, one decimal; null for a first rate or a change from zero. */
  percentChange: number | null;
  unit: string | null;
  text: string;
}

export function rateChangeSummary(rate: Pick<ResourceRate, 'rate' | 'supersedes' | 'resource'>): RateChangeSummary {
  const prior = rate.supersedes?.rate ?? null;
  const unit = rate.resource?.unit ?? null;
  const per = unit ? ` / ${perUnit(unit)}` : '';
  const percentChange =
    prior !== null && prior !== 0 ? Math.round(((rate.rate - prior) / prior) * 1000) / 10 : null;
  const pct =
    percentChange === null ? '' : ` (${percentChange > 0 ? '+' : ''}${percentChange.toFixed(1)}%)`;
  return {
    isNew: prior === null,
    priorRate: prior,
    newRate: rate.rate,
    percentChange,
    unit,
    text:
      prior === null
        ? `new rate ${currency(rate.rate)}${per}`
        : `${currency(prior)} → ${currency(rate.rate)}${per}${pct}`,
  };
}

/** "today", "1 day", "3 days" - how long a request has been waiting. */
export function requestAge(createdAt: string | null | undefined, now: Date = new Date()): string {
  if (!createdAt) return '–';
  const then = new Date(createdAt);
  if (Number.isNaN(then.getTime())) return '–';
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.max(0, Math.round((startOf(now) - startOf(then)) / 86_400_000));
  if (days === 0) return 'today';
  return `${days} day${days === 1 ? '' : 's'}`;
}
